/**
 * Activity-session heartbeat (audit finding H5).
 *
 * Sends a periodic "still here" ping to the `recordSessionHeartbeat` Cloud
 * Function while the app is open and the tab is visible. The ping carries no
 * timing data — the server measures duration with its own clock, because the
 * same measurement gates parental screen-time limits and a self-reported
 * number would be worthless there. See functions/src/sessionFunctions.ts.
 *
 * Mount this ONCE, high in the tree (App.tsx), not per-route: a second
 * instance would double the ping rate without recording any more time, and
 * would burn quota.
 *
 * Cost: one callable invocation per user per two minutes of active use, and
 * only while the tab is visible.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { createComponentLogger } from '../utils/logger';
import { functions } from '../config/firebase';

const logger = createComponentLogger('ActivitySession');

/**
 * How often to ping. Must stay comfortably under the server's
 * SESSION_IDLE_TIMEOUT_MS (10 min) so a continuously-open tab reads as one
 * session, and above MIN_HEARTBEAT_INTERVAL_MS (20 s) so pings are not
 * rejected as replays.
 */
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

/**
 * Backoff after a failed ping, so a Function outage or a missing deployment
 * does not turn into a tight retry loop on every client.
 */
const FAILURE_BACKOFF_MS = 10 * 60 * 1000;

export interface ActivitySessionState {
  /** Minutes of active use recorded today, per the server. */
  todayMinutes: number;
  /** Minutes accumulated in the current sitting. */
  sessionMinutes: number;
  /** True once at least one heartbeat has been acknowledged. */
  tracking: boolean;
}

interface HeartbeatResponse {
  sessionId: string;
  sessionMinutes: number;
  todayMinutes: number;
  startedNewSession: boolean;
}

export const useActivitySession = (): ActivitySessionState => {
  const { currentUser } = useAuth();
  const [state, setState] = useState<ActivitySessionState>({
    todayMinutes: 0,
    sessionMinutes: 0,
    tracking: false,
  });

  // Guards against overlapping pings when a slow call spans a tick.
  const inFlight = useRef(false);
  // Timestamp before which we should not retry, after a failure.
  const backoffUntil = useRef(0);

  const sendHeartbeat = useCallback(async () => {
    if (!currentUser || inFlight.current) return;
    if (Date.now() < backoffUntil.current) return;

    inFlight.current = true;
    try {
      const callable = httpsCallable<Record<string, never>, HeartbeatResponse>(
        functions.value,
        'recordSessionHeartbeat'
      );
      const { data } = await callable({});
      setState({
        todayMinutes: data.todayMinutes ?? 0,
        sessionMinutes: data.sessionMinutes ?? 0,
        tracking: true,
      });
    } catch (err) {
      // A missing/undeployed function must never break the app, and must not
      // retry every two minutes forever.
      backoffUntil.current = Date.now() + FAILURE_BACKOFF_MS;
      logger.warn('Activity heartbeat failed; backing off', err);
    } finally {
      inFlight.current = false;
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setState({ todayMinutes: 0, sessionMinutes: 0, tracking: false });
      return;
    }

    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer !== null) return;
      void sendHeartbeat(); // establish the session immediately
      timer = setInterval(() => void sendHeartbeat(), HEARTBEAT_INTERVAL_MS);
    };

    const stop = () => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    // Only count time the tab is actually visible. A backgrounded tab is not
    // screen time, and counting it would inflate every engagement metric.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        start();
      } else {
        stop();
        // Credit the time up to the moment of leaving, so closing the tab
        // does not silently discard the last interval.
        void sendHeartbeat();
      }
    };

    if (document.visibilityState === 'visible') {
      start();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stop();
    };
  }, [currentUser, sendHeartbeat]);

  return state;
};
